import { useEffect } from 'react'
import { AnyAction } from '@reduxjs/toolkit'
import { isRaceDetails } from '@mobi/betslip/helpers/typeGuards'
import { store } from '@core/Store'
import { ensureBetErrorTypeLiteral } from '@core/Data/betting'
import type {
  BetSlipItem,
  BetSlipResponse,
  EventDetails,
  FobBetReceiptResponse,
} from '@mobi/betslip/types'
import { confirmAllBets, proposeAllBets } from '@mobi/betslip/Store/Workflow/asyncActions'
import { startListening } from '@core/Store/listenerMiddleware'
import { trackBetConfirmation } from '@classic/Foundation/Analytics/Analytics.Bet'
import {
  keys as analyticsKeys,
  defaultData as analyticsDefaultData,
  AnalyticsBetData,
} from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import * as Analytics from '@classic/Foundation/Analytics/Analytics'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import type { FeatureFlagsState } from '@core/State/FeatureFlags/Store'
import { trackShareBetLinkPlaced } from '@core/Areas/ShareBet/helpers/analytics'

export function useBetSlipAnalytics(shouldEnable: boolean): void {
  useEffect(() => {
    if (!shouldEnable) return

    const listeners = [
      startListening({
        actionCreator: proposeAllBets.fulfilled,
        effect: handleProposeAllBetsSuccessful,
      }),
      startListening({
        actionCreator: proposeAllBets.rejected,
        effect: handleProposeAllBetsFailed,
      }),
      startListening({
        actionCreator: confirmAllBets.fulfilled,
        effect: handleConfirmAllBetsSuccessful,
      }),
      startListening({
        actionCreator: confirmAllBets.rejected,
        effect: handleConfirmAllBetsFailed,
      }),
    ]

    return () => {
      listeners.forEach(stopListener => stopListener())
    }
  }, [shouldEnable])
}

// =============
// Local Helpers
// =============

const handleProposeAllBetsSuccessful = (action: { payload: BetSlipResponse[] }) => {
  const trackData: AnalyticsBetData = { ...analyticsDefaultData.bet, construction: 'betslip' }
  action.payload.forEach(bet => {
    const errorType = bet.error?.type || bet.legs?.[0]?.error?.type

    Analytics.track(analyticsKeys.betAccountSelected, {
      bet: {
        ...trackData,
        productType: bet.legs && bet.legs?.length > 1 ? 'multi' : 'single',
        errorMessage: mapBetErrorTypeIfNonNullable(errorType),
      },
    })
  })
}

const handleProposeAllBetsFailed = (action: AnyAction) => {
  const errorMessage = mapBetErrorTypeIfNonNullable(action.payload.code || 'Network Error')
  const step1BetData: AnalyticsBetData = {
    ...analyticsDefaultData.bet,
    construction: 'betslip',
    productType: 'N/A',
    errorMessage,
  }
  Analytics.track(analyticsKeys.betAccountSelected, { bet: step1BetData })
}

const handleConfirmAllBetsSuccessful = (action: { payload: BetSlipResponse[] }) => {
  const betslipStateDataItems = store.getState().betslip.bets.items

  action.payload.forEach(bet => {
    const isMultiBet = bet.legs && bet.legs?.length > 1
    const betType = isMultiBet ? 'multi' : 'single'

    const step2BetData: AnalyticsBetData = {
      ...analyticsDefaultData.bet,
      construction: 'betslip',
      productType: betType,
      errorMessage: !bet.success && bet.error ? mapBetErrorTypeIfNonNullable(bet.error.type) : null,
    }
    Analytics.track(analyticsKeys.betAccountReview, { bet: step2BetData })

    if (bet.success) {
      if (isMultiBet) {
        trackMultiBetConfirmation(bet, betslipStateDataItems)
      } else {
        trackSingleBetConfirmation(bet, betslipStateDataItems, getFeatures(store.getState()))
      }
    }
  })
}

const handleConfirmAllBetsFailed = (action: AnyAction) => {
  const errorMessage = mapBetErrorTypeIfNonNullable(action.payload.code || 'Network Error')
  const step2BetData: AnalyticsBetData = {
    ...analyticsDefaultData.bet,
    construction: 'betslip',
    productType: 'N/A',
    errorMessage,
  }
  Analytics.track(analyticsKeys.betAccountReview, { bet: step2BetData })
}

const trackMultiBetConfirmation = (bet: BetSlipResponse, betslipStateItems: BetSlipItem[]) => {
  const { ticketNumber, winInvestment, bonusBet } = bet.receipt as FobBetReceiptResponse
  const step3MultiBetData: AnalyticsBetData = {
    ...analyticsDefaultData.bet,
    specialOffers: null,
    id: ticketNumber,
    construction: 'betslip',
    productType: 'multi',
    placementMethod: 'account',
    mixed: {
      fixed: {
        cost: winInvestment,
        numberOfTickets: 1,
        bonusCashUsed: bonusBet > 0,
        bonusCashAmount: bonusBet,
      },
    },
  }
  Analytics.track(analyticsKeys.betAccountProcessed, { bet: step3MultiBetData })

  if (bet.legs) {
    const multiLegIds = bet.legs.map(({ id }) => id)
    const matchedMultiBetSlipItems = betslipStateItems.filter(item => multiLegIds.includes(item.id))
    const multiShareOrigins = matchedMultiBetSlipItems.flatMap(item =>
      item.betSourceShareOrigin ? [item.betSourceShareOrigin] : []
    )

    if (multiShareOrigins.length > 0) {
      const shareOrigins = new Set([...multiShareOrigins])
      const meetingCode = matchedMultiBetSlipItems.reduce((prev, curr) => {
        const currMeetingCode = getMeetingCode(curr.selectionDetails)
        return !prev || prev === currMeetingCode ? currMeetingCode : 'other'
      }, '')

      trackShareBetLinkPlaced({
        raceCode: meetingCode.toLowerCase(),
        hasSpecialOffer: false,
        amountPaid: winInvestment,
        legs: multiShareOrigins.length,
        origin: shareOrigins.size > 1 ? 'mixed' : Array.from(shareOrigins)[0],
      })
    }
  }
}

const trackSingleBetConfirmation = (
  bet: BetSlipResponse,
  betslipStateDataItems: BetSlipItem[],
  featureFlags: FeatureFlagsState
) => {
  const matchedItem = betslipStateDataItems.find(item => item.id === bet.id)
  if (!matchedItem) return

  const { selection, selectionDetails, betSource, betSourceShareOrigin } = matchedItem
  const { ticketNumber, winInvestment, placeInvestment, specialOffers, betCost, bonusBet } =
    extractReceipt(bet.receipt)

  trackBetConfirmation({
    ticketNumber,
    selection,
    selectionDetails,
    specialOffers,
    betConstruction: 'betslip',
    betCost,
    winInvestment,
    placeInvestment,
    features: featureFlags,
    betSource,
    betSourceShareOrigin,
    bonusCashAmount: bonusBet,
  })
}

const mapBetErrorTypeIfNonNullable = (betErrorType: unknown): Nullable<string> =>
  typeof betErrorType === 'string' || typeof betErrorType === 'number'
    ? ensureBetErrorTypeLiteral(betErrorType)
    : null

function extractReceipt(receipt: BetSlipResponse['receipt']) {
  if (!receipt) {
    return {
      ticketNumber: -1,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: 0,
      bonusBet: 0,
    }
  }

  if ('betCost' in receipt) {
    return {
      ticketNumber: receipt.ticketNumber,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: receipt.betCost,
      bonusBet: receipt.bonusBet,
    }
  }

  return {
    ticketNumber: receipt.ticketNumber,
    winInvestment: receipt.winInvestment,
    placeInvestment: receipt.placeInvestment,
    specialOffers: receipt.specialOffers ?? [],
    betCost: 0,
    bonusBet: receipt.bonusBet,
  }
}

function getMeetingCode(selectionDetails: EventDetails | null): string {
  if (!selectionDetails || !isRaceDetails(selectionDetails)) return 'other'
  return selectionDetails.races[0]?.meetingCode || 'other'
}
